import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Stage, Layer, Rect, Circle, Text, Transformer, Star, Line, Arrow, Image as KonvaImage } from 'react-konva';
import { exportStageSVG } from 'react-konva-to-svg';
import { 
  Square, Circle as CircleIcon, Star as StarIcon, Type, Minus, ArrowRight,
  Trash2, Copy, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Download, Save, Upload,
  Undo, Redo, ZoomIn, ZoomOut, MousePointer, Image as ImageIcon,
  RotateCcw, Lock, Unlock, Eye, EyeOff, Grid3X3, 
  PenTool, Calculator, Check, CornerUpRight, Link, Link2Off, TypeOutline, Plus
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// Unit conversions
const UNITS = {
  'mm': { name: 'Millimeters', symbol: 'mm', toMM: 1 },
  'cm': { name: 'Centimeters', symbol: 'cm', toMM: 10 },
  'in': { name: 'Inches', symbol: 'in', toMM: 25.4 },
  'px': { name: 'Pixels', symbol: 'px', toMM: 25.4 / 96 },
};

// Page size presets (in mm)
const PAGE_SIZES = {
  'custom': { name: 'Custom', width: 210, height: 148 },
  'a4-landscape': { name: 'A4 Landscape', width: 297, height: 210 },
  'a4-portrait': { name: 'A4 Portrait', width: 210, height: 297 },
  'a3-landscape': { name: 'A3 Landscape', width: 420, height: 297 },
  'a3-portrait': { name: 'A3 Portrait', width: 297, height: 420 },
  'mini-bonnet': { name: 'Mini Bonnet', width: 800, height: 600 },
  'mini-roof': { name: 'Mini Roof', width: 1200, height: 800 },
  'mini-side': { name: 'Mini Side Panel', width: 1500, height: 400 },
};

const FONTS = [
  'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
  'Courier New', 'Impact', 'Comic Sans MS', 'Trebuchet MS',
  'Arial Black', 'Palatino', 'Garamond', 'Tahoma', 'Lucida Console',
];

// 40 Spot Rainbow Colors with names
const SPOT_COLORS = [
  { name: 'Black', hex: '#000000' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Silver', hex: '#C0C0C0' },
  { name: 'Grey', hex: '#808080' },
  { name: 'Charcoal', hex: '#36454F' },
  { name: 'Red', hex: '#FF0000' },
  { name: 'Crimson', hex: '#DC143C' },
  { name: 'Maroon', hex: '#800000' },
  { name: 'Scarlet', hex: '#FF2400' },
  { name: 'Cherry', hex: '#DE3163' },
  { name: 'Orange', hex: '#FF8C00' },
  { name: 'Tangerine', hex: '#FF9966' },
  { name: 'Coral', hex: '#FF7F50' },
  { name: 'Peach', hex: '#FFCBA4' },
  { name: 'Yellow', hex: '#FFFF00' },
  { name: 'Gold', hex: '#FFD700' },
  { name: 'Lemon', hex: '#FFF44F' },
  { name: 'Mustard', hex: '#FFDB58' },
  { name: 'Lime', hex: '#32CD32' },
  { name: 'Green', hex: '#008000' },
  { name: 'Forest', hex: '#228B22' },
  { name: 'Emerald', hex: '#50C878' },
  { name: 'Mint', hex: '#98FB98' },
  { name: 'Teal', hex: '#008080' },
  { name: 'Cyan', hex: '#00FFFF' },
  { name: 'Sky Blue', hex: '#87CEEB' },
  { name: 'Blue', hex: '#0000FF' },
  { name: 'Navy', hex: '#000080' },
  { name: 'Royal', hex: '#4169E1' },
  { name: 'Cobalt', hex: '#0047AB' },
  { name: 'Purple', hex: '#800080' },
  { name: 'Violet', hex: '#EE82EE' },
  { name: 'Lavender', hex: '#E6E6FA' },
  { name: 'Plum', hex: '#DDA0DD' },
  { name: 'Indigo', hex: '#4B0082' },
  { name: 'Pink', hex: '#FFC0CB' },
  { name: 'Hot Pink', hex: '#FF69B4' },
  { name: 'Magenta', hex: '#FF00FF' },
  { name: 'Rose', hex: '#FF007F' },
  { name: 'Brown', hex: '#8B4513' },
];

const PRICE_PER_SQM = 50;
const MIN_ORDER = 10;

const mmToPx = (mm) => (mm / 25.4) * 96;
const pxToMM = (px) => (px / 96) * 25.4;
const toDisplayUnit = (valueMM, unit) => valueMM / UNITS[unit].toMM;
const fromDisplayUnit = (value, unit) => value * UNITS[unit].toMM;

const DesignStudio = ({ onExport }) => {
  const [pageSize, setPageSize] = useState('custom');
  const [canvasWidthMM, setCanvasWidthMM] = useState(210);
  const [canvasHeightMM, setCanvasHeightMM] = useState(148);
  const [displayUnit, setDisplayUnit] = useState('mm');
  
  const [shapes, setShapes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  const [zoom, setZoom] = useState(1);
  const [tool, setTool] = useState('select');
  const [viewMode, setViewMode] = useState('full');
  const [showGrid, setShowGrid] = useState(false);
  
  const [drawingColor, setDrawingColor] = useState('#000000');
  const [selectedColorName, setSelectedColorName] = useState('Black');
  const [strokeWidth, setStrokeWidth] = useState(2);
  
  const [proportionLocked, setProportionLocked] = useState(true);
  const [selectedNodeIndex, setSelectedNodeIndex] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  
  // Live resize display
  const [isResizing, setIsResizing] = useState(false);
  const [liveResizeSize, setLiveResizeSize] = useState(null);
  
  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const trRef = useRef(null);

  const canvasWidth = mmToPx(canvasWidthMM);
  const canvasHeight = mmToPx(canvasHeightMM);
  const selectedShape = shapes.find(s => s.id === selectedId);

  // Pricing calculation
  const pricingInfo = useMemo(() => {
    if (shapes.length === 0) return { boundingBox: null, areaMM2: 0, areaSQM: 0, price: 0 };

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    shapes.forEach(shape => {
      if (!shape.visible) return;
      let shapeMinX, shapeMinY, shapeMaxX, shapeMaxY;
      
      switch (shape.type) {
        case 'rect':
          shapeMinX = shape.x; shapeMinY = shape.y;
          shapeMaxX = shape.x + shape.width; shapeMaxY = shape.y + shape.height;
          break;
        case 'circle':
          shapeMinX = shape.x - shape.radius; shapeMinY = shape.y - shape.radius;
          shapeMaxX = shape.x + shape.radius; shapeMaxY = shape.y + shape.radius;
          break;
        case 'star':
          shapeMinX = shape.x - shape.outerRadius; shapeMinY = shape.y - shape.outerRadius;
          shapeMaxX = shape.x + shape.outerRadius; shapeMaxY = shape.y + shape.outerRadius;
          break;
        case 'text':
          const textWidth = (shape.text?.length || 0) * (shape.fontSize * 0.6);
          shapeMinX = shape.x; shapeMinY = shape.y;
          shapeMaxX = shape.x + textWidth; shapeMaxY = shape.y + shape.fontSize;
          break;
        default:
          if (shape.points) {
            for (let i = 0; i < shape.points.length; i += 2) {
              const px = shape.x + shape.points[i];
              const py = shape.y + shape.points[i + 1];
              if (px < minX) minX = px; if (py < minY) minY = py;
              if (px > maxX) maxX = px; if (py > maxY) maxY = py;
            }
          }
          return;
      }
      if (shapeMinX < minX) minX = shapeMinX; if (shapeMinY < minY) minY = shapeMinY;
      if (shapeMaxX > maxX) maxX = shapeMaxX; if (shapeMaxY > maxY) maxY = shapeMaxY;
    });

    if (minX === Infinity) return { boundingBox: null, areaMM2: 0, areaSQM: 0, price: 0 };

    const widthMM = pxToMM(maxX - minX);
    const heightMM = pxToMM(maxY - minY);
    const areaMM2 = widthMM * heightMM;
    const areaSQM = areaMM2 / 1000000;

    return { widthMM, heightMM, areaMM2, areaSQM, price: Math.max(areaSQM * PRICE_PER_SQM, MIN_ORDER) };
  }, [shapes]);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    if (size !== 'custom') {
      setCanvasWidthMM(PAGE_SIZES[size].width);
      setCanvasHeightMM(PAGE_SIZES[size].height);
    }
  }, []);

  const saveToHistory = useCallback((newShapes) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newShapes)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setShapes(JSON.parse(JSON.stringify(history[historyIndex - 1])));
      setSelectedId(null);
    }
  }, [historyIndex, history]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setShapes(JSON.parse(JSON.stringify(history[historyIndex + 1])));
      setSelectedId(null);
    }
  }, [historyIndex, history]);

  const handleColorSelect = useCallback((color) => {
    setDrawingColor(color.hex);
    setSelectedColorName(color.name);
    if (selectedId) {
      const newShapes = shapes.map(shape =>
        shape.id === selectedId ? { ...shape, fill: color.hex, stroke: color.hex } : shape
      );
      setShapes(newShapes);
    }
  }, [selectedId, shapes]);

  // Add shape at click position
  const addShapeAtPosition = useCallback((type, x, y) => {
    const baseShape = {
      id: `shape_${Date.now()}`,
      type,
      x, y,
      fill: type === 'line' || type === 'arrow' || type === 'path' ? 'transparent' : drawingColor,
      stroke: drawingColor,
      strokeWidth,
      draggable: true,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      visible: true,
      locked: false,
      name: `${type.charAt(0).toUpperCase() + type.slice(1)} ${shapes.length + 1}`,
      tension: 0,
    };

    let newShape;
    switch (type) {
      case 'rect':
        newShape = { ...baseShape, width: mmToPx(50), height: mmToPx(30), cornerRadius: 0 };
        break;
      case 'circle':
        newShape = { ...baseShape, radius: mmToPx(25) };
        break;
      case 'star':
        newShape = { ...baseShape, numPoints: 5, innerRadius: mmToPx(12), outerRadius: mmToPx(25) };
        break;
      case 'line':
        newShape = { ...baseShape, points: [0, 0, mmToPx(50), 0], fill: 'transparent' };
        break;
      case 'arrow':
        newShape = { ...baseShape, points: [0, 0, mmToPx(50), 0], fill: 'transparent', pointerLength: 10, pointerWidth: 10 };
        break;
      case 'path':
        newShape = { ...baseShape, points: [0, 0, mmToPx(25), mmToPx(-20), mmToPx(50), 0], fill: 'transparent', tension: 0.5 };
        break;
      case 'text':
        newShape = { ...baseShape, text: 'Text', fontSize: 32, fontFamily: 'Arial', fontStyle: 'normal', textDecoration: '', fill: drawingColor };
        break;
      default:
        return;
    }

    const newShapes = [...shapes, newShape];
    setShapes(newShapes);
    saveToHistory(newShapes);
    setSelectedId(newShape.id);
    setTool('select');
  }, [shapes, drawingColor, strokeWidth, saveToHistory]);

  // Handle canvas click - add shape or select
  const handleCanvasClick = useCallback((e) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    
    if (clickedOnEmpty) {
      if (tool !== 'select' && tool !== 'nodes') {
        // Get click position relative to stage
        const stage = e.target.getStage();
        const pos = stage.getPointerPosition();
        const x = pos.x / zoom;
        const y = pos.y / zoom;
        addShapeAtPosition(tool, x, y);
      } else {
        setSelectedId(null);
        setSelectedNodeIndex(null);
        if (trRef.current) {
          trRef.current.nodes([]);
          trRef.current.getLayer().batchDraw();
        }
      }
    }
  }, [tool, zoom, addShapeAtPosition]);

  const handleSelect = useCallback((id) => {
    if (tool !== 'select' && tool !== 'nodes') return;
    const shape = shapes.find(s => s.id === id);
    if (shape?.locked) return;
    
    setSelectedId(id);
    setSelectedNodeIndex(null);
    
    if (trRef.current && stageRef.current && tool === 'select') {
      const node = stageRef.current.findOne(`#${id}`);
      if (node) {
        trRef.current.nodes([node]);
        trRef.current.getLayer().batchDraw();
      }
    }
  }, [tool, shapes]);

  const updateSelectedStyle = useCallback((key, value) => {
    if (!selectedId) return;
    setShapes(shapes.map(shape => shape.id === selectedId ? { ...shape, [key]: value } : shape));
  }, [selectedId, shapes]);

  // Size adjustment with arrows
  const adjustSize = useCallback((dimension, delta) => {
    if (!selectedId) return;
    const shape = shapes.find(s => s.id === selectedId);
    if (!shape) return;

    const increment = fromDisplayUnit(delta, displayUnit);
    const incrementPx = mmToPx(increment);

    let updates = {};
    if (shape.width !== undefined && shape.height !== undefined) {
      const currentAspect = shape.width / shape.height;
      if (dimension === 'width') {
        const newWidth = Math.max(mmToPx(5), shape.width + incrementPx);
        updates.width = newWidth;
        if (proportionLocked) updates.height = newWidth / currentAspect;
      } else {
        const newHeight = Math.max(mmToPx(5), shape.height + incrementPx);
        updates.height = newHeight;
        if (proportionLocked) updates.width = newHeight * currentAspect;
      }
    } else if (shape.radius !== undefined) {
      updates.radius = Math.max(mmToPx(2), shape.radius + incrementPx);
    }

    const newShapes = shapes.map(s => s.id === selectedId ? { ...s, ...updates } : s);
    setShapes(newShapes);
  }, [selectedId, shapes, proportionLocked, displayUnit]);

  const updateSizeWithProportion = useCallback((dimension, value) => {
    if (!selectedId) return;
    const shape = shapes.find(s => s.id === selectedId);
    if (!shape) return;

    let updates = {};
    if (shape.width !== undefined && shape.height !== undefined) {
      const currentAspect = shape.width / shape.height;
      if (dimension === 'width') {
        updates.width = value;
        if (proportionLocked) updates.height = value / currentAspect;
      } else {
        updates.height = value;
        if (proportionLocked) updates.width = value * currentAspect;
      }
    } else if (shape.radius !== undefined) {
      updates.radius = value;
    }

    setShapes(shapes.map(s => s.id === selectedId ? { ...s, ...updates } : s));
  }, [selectedId, shapes, proportionLocked]);

  const updateNodePosition = useCallback((shapeId, nodeIndex, x, y) => {
    const shape = shapes.find(s => s.id === shapeId);
    if (!shape || !shape.points) return;
    const newPoints = [...shape.points];
    newPoints[nodeIndex * 2] = x;
    newPoints[nodeIndex * 2 + 1] = y;
    setShapes(shapes.map(s => s.id === shapeId ? { ...s, points: newPoints } : s));
  }, [shapes]);

  const toggleNodeCurve = useCallback(() => {
    if (!selectedId) return;
    const shape = shapes.find(s => s.id === selectedId);
    if (!shape) return;
    const newTension = shape.tension > 0 ? 0 : 0.5;
    setShapes(shapes.map(s => s.id === selectedId ? { ...s, tension: newTension } : s));
    saveToHistory(shapes);
    toast.success(newTension > 0 ? 'Curves enabled' : 'Straight lines');
  }, [selectedId, shapes, saveToHistory]);

  // Add node - if a node is selected, insert between selected and next node, otherwise append
  const addNode = useCallback(() => {
    if (!selectedId) return;
    const shape = shapes.find(s => s.id === selectedId);
    if (!shape?.points || shape.points.length < 4) return;
    
    let newPoints;
    if (selectedNodeIndex !== null && selectedNodeIndex < shape.points.length / 2 - 1) {
      // Insert between selected node and next node
      const idx = selectedNodeIndex * 2;
      const x1 = shape.points[idx], y1 = shape.points[idx + 1];
      const x2 = shape.points[idx + 2], y2 = shape.points[idx + 3];
      // Midpoint
      const midX = (x1 + x2) / 2, midY = (y1 + y2) / 2;
      newPoints = [
        ...shape.points.slice(0, idx + 2),
        midX, midY,
        ...shape.points.slice(idx + 2)
      ];
    } else {
      // Append at end
      const lastX = shape.points[shape.points.length - 2];
      const lastY = shape.points[shape.points.length - 1];
      newPoints = [...shape.points, lastX + 30, lastY];
    }
    
    const newShapes = shapes.map(s => s.id === selectedId ? { ...s, points: newPoints } : s);
    setShapes(newShapes);
    saveToHistory(newShapes);
    toast.success('Node added');
  }, [selectedId, selectedNodeIndex, shapes, saveToHistory]);

  const removeNode = useCallback(() => {
    if (!selectedId || selectedNodeIndex === null) return;
    const shape = shapes.find(s => s.id === selectedId);
    if (!shape?.points || shape.points.length <= 4) return;
    const newPoints = [...shape.points];
    newPoints.splice(selectedNodeIndex * 2, 2);
    const newShapes = shapes.map(s => s.id === selectedId ? { ...s, points: newPoints } : s);
    setShapes(newShapes);
    saveToHistory(newShapes);
    setSelectedNodeIndex(null);
  }, [selectedId, selectedNodeIndex, shapes, saveToHistory]);

  // Convert text to curves (vector path)
  const convertTextToCurves = useCallback(() => {
    if (!selectedId) return;
    const shape = shapes.find(s => s.id === selectedId);
    if (!shape || shape.type !== 'text') return;
    
    // Create an offscreen canvas to measure text
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const fontStyle = shape.fontStyle || 'normal';
    ctx.font = `${fontStyle} ${shape.fontSize}px ${shape.fontFamily}`;
    const metrics = ctx.measureText(shape.text || 'Text');
    const textWidth = metrics.width;
    const textHeight = shape.fontSize;
    
    // Generate outline points for the text bounding box (simplified approach)
    // Since we can't get actual glyph outlines in browser, we create a shape that represents the text bounds
    // with more control points for editing
    const padding = 2;
    const x = 0, y = 0;
    const w = textWidth + padding * 2;
    const h = textHeight + padding * 2;
    
    // Create a path with multiple nodes for finer control
    const segments = Math.max(4, Math.ceil(textWidth / 20)); // More segments for longer text
    const points = [];
    
    // Top edge (left to right)
    for (let i = 0; i <= segments; i++) {
      points.push(x + (w * i / segments), y - h * 0.3);
    }
    // Right edge
    points.push(x + w, y + h * 0.7);
    // Bottom edge (right to left)  
    for (let i = segments; i >= 0; i--) {
      points.push(x + (w * i / segments), y + h * 0.7);
    }
    // Left edge back to start
    points.push(x, y - h * 0.3);
    
    const newShape = {
      id: `path_${Date.now()}`,
      type: 'path',
      x: shape.x,
      y: shape.y + textHeight * 0.3,
      points,
      fill: shape.fill || '#000000',
      stroke: shape.stroke || shape.fill || '#000000',
      strokeWidth: shape.strokeWidth || 1,
      draggable: true,
      rotation: shape.rotation || 0,
      scaleX: 1,
      scaleY: 1,
      visible: true,
      locked: false,
      name: `${shape.text} (curves)`,
      tension: 0,
    };
    
    // Replace text with path
    const newShapes = shapes.map(s => s.id === selectedId ? newShape : s);
    setShapes(newShapes);
    saveToHistory(newShapes);
    setSelectedId(newShape.id);
    toast.success('Text converted to curves - edit nodes to reshape');
  }, [selectedId, shapes, saveToHistory]);

  // Handle transform with live size display
  const handleTransform = useCallback((e) => {
    const node = e.target;
    let w, h;
    
    if (node.attrs.width) {
      w = node.width() * node.scaleX();
      h = node.height() * node.scaleY();
    } else if (node.attrs.radius) {
      w = h = node.radius() * 2 * Math.max(node.scaleX(), node.scaleY());
    }
    
    if (w && h) {
      const wDisplay = toDisplayUnit(pxToMM(w), displayUnit).toFixed(1);
      const hDisplay = toDisplayUnit(pxToMM(h), displayUnit).toFixed(1);
      setLiveResizeSize({ w: wDisplay, h: hDisplay });
      setIsResizing(true);
    }
  }, [displayUnit]);

  const handleTransformEnd = useCallback((e) => {
    const node = e.target;
    const id = node.id();
    
    const updates = {
      x: node.x(),
      y: node.y(),
      rotation: node.rotation(),
    };

    if (node.attrs.width) {
      updates.width = Math.max(5, node.width() * node.scaleX());
      updates.height = Math.max(5, node.height() * node.scaleY());
      updates.scaleX = 1;
      updates.scaleY = 1;
    } else if (node.attrs.radius) {
      updates.radius = Math.max(5, node.radius() * Math.max(node.scaleX(), node.scaleY()));
      updates.scaleX = 1;
      updates.scaleY = 1;
    }

    const newShapes = shapes.map(shape => shape.id === id ? { ...shape, ...updates } : shape);
    setShapes(newShapes);
    saveToHistory(newShapes);
    setIsResizing(false);
    setLiveResizeSize(null);
  }, [shapes, saveToHistory]);

  const handleDragEnd = useCallback((e) => {
    const id = e.target.id();
    const newShapes = shapes.map(shape => shape.id === id ? { ...shape, x: e.target.x(), y: e.target.y() } : shape);
    setShapes(newShapes);
    saveToHistory(newShapes);
  }, [shapes, saveToHistory]);

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    const newShapes = shapes.filter(shape => shape.id !== selectedId);
    setShapes(newShapes);
    saveToHistory(newShapes);
    setSelectedId(null);
    // Clear transformer to remove visual artifact
    if (trRef.current) {
      trRef.current.nodes([]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [selectedId, shapes, saveToHistory]);

  const duplicateSelected = useCallback(() => {
    if (!selectedId) return;
    const shape = shapes.find(s => s.id === selectedId);
    if (!shape) return;
    const newShape = { ...JSON.parse(JSON.stringify(shape)), id: `shape_${Date.now()}`, x: shape.x + 20, y: shape.y + 20, name: `${shape.name} Copy` };
    const newShapes = [...shapes, newShape];
    setShapes(newShapes);
    saveToHistory(newShapes);
    setSelectedId(newShape.id);
  }, [selectedId, shapes, saveToHistory]);

  const bringToFront = useCallback(() => {
    if (!selectedId) return;
    const index = shapes.findIndex(s => s.id === selectedId);
    if (index === shapes.length - 1) return;
    const newShapes = [...shapes];
    const [shape] = newShapes.splice(index, 1);
    newShapes.push(shape);
    setShapes(newShapes);
    saveToHistory(newShapes);
  }, [selectedId, shapes, saveToHistory]);

  const sendToBack = useCallback(() => {
    if (!selectedId) return;
    const index = shapes.findIndex(s => s.id === selectedId);
    if (index === 0) return;
    const newShapes = [...shapes];
    const [shape] = newShapes.splice(index, 1);
    newShapes.unshift(shape);
    setShapes(newShapes);
    saveToHistory(newShapes);
  }, [selectedId, shapes, saveToHistory]);

  const toggleLock = useCallback(() => {
    if (!selectedId) return;
    updateSelectedStyle('locked', !shapes.find(s => s.id === selectedId)?.locked);
    setSelectedId(null);
  }, [selectedId, shapes, updateSelectedStyle]);

  const toggleVisibility = useCallback(() => {
    if (!selectedId) return;
    updateSelectedStyle('visible', !shapes.find(s => s.id === selectedId)?.visible);
  }, [selectedId, shapes, updateSelectedStyle]);

  // Export functions
  const exportSVG = useCallback(async () => {
    if (!stageRef.current) return;
    try {
      if (trRef.current) trRef.current.nodes([]);
      setSelectedId(null);
      await new Promise(resolve => setTimeout(resolve, 100));
      const svgString = await exportStageSVG(stageRef.current, false);
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'my-design.svg';
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Design exported as SVG!');
      if (onExport) onExport(svgString, blob);
    } catch (error) {
      toast.error('Failed to export');
    }
  }, [onExport]);

  const exportPNG = useCallback(() => {
    if (!stageRef.current) return;
    try {
      if (trRef.current) { trRef.current.nodes([]); trRef.current.getLayer().batchDraw(); }
      const dataURL = stageRef.current.toDataURL({ pixelRatio: 2 });
      const link = document.createElement('a');
      link.download = 'my-design.png';
      link.href = dataURL;
      link.click();
      toast.success('Design exported as PNG!');
    } catch (error) {
      toast.error('Failed to export');
    }
  }, []);

  const saveDesign = useCallback(() => {
    const designData = { shapes, canvasWidthMM, canvasHeightMM, pageSize, displayUnit, version: '3.0' };
    const blob = new Blob([JSON.stringify(designData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'my-design.json';
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Design saved!');
  }, [shapes, canvasWidthMM, canvasHeightMM, pageSize, displayUnit]);

  const loadDesign = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (data.shapes) {
          setShapes(data.shapes);
          saveToHistory(data.shapes);
          setSelectedId(null);
          if (data.canvasWidthMM) setCanvasWidthMM(data.canvasWidthMM);
          if (data.canvasHeightMM) setCanvasHeightMM(data.canvasHeightMM);
          if (data.pageSize) setPageSize(data.pageSize);
          if (data.displayUnit) setDisplayUnit(data.displayUnit);
          toast.success('Design loaded!');
        }
      } catch { toast.error('Invalid file'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [saveToHistory]);

  const importImage = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new window.Image();
      img.onload = () => {
        const scale = Math.min(mmToPx(100) / img.width, mmToPx(100) / img.height, 1);
        const newShape = {
          id: `image_${Date.now()}`, type: 'image', x: canvasWidth / 2, y: canvasHeight / 2,
          image: img, width: img.width * scale, height: img.height * scale,
          draggable: true, rotation: 0, scaleX: 1, scaleY: 1, visible: true, locked: false, name: `Image ${shapes.length + 1}`
        };
        const newShapes = [...shapes, newShape];
        setShapes(newShapes);
        saveToHistory(newShapes);
        setSelectedId(newShape.id);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [shapes, saveToHistory, canvasWidth, canvasHeight]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault(); // Prevent browser back navigation
        if (selectedId) {
          deleteSelected();
        }
      }
      else if (e.key === 'Escape') { setSelectedId(null); setSelectedNodeIndex(null); setTool('select'); }
      else if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key === 'd') { e.preventDefault(); duplicateSelected(); }
      else if (e.key === 'v') setTool('select');
      else if (e.key === 'n') setTool('nodes');
      else if (e.key === 'g') setShowGrid(!showGrid);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteSelected, undo, redo, duplicateSelected, showGrid, selectedId]);

  // Live size for selected shape
  const getLiveSize = useCallback(() => {
    if (isResizing && liveResizeSize) return liveResizeSize;
    if (!selectedShape) return null;
    let w, h;
    if (selectedShape.width !== undefined) {
      w = toDisplayUnit(pxToMM(selectedShape.width), displayUnit);
      h = toDisplayUnit(pxToMM(selectedShape.height), displayUnit);
    } else if (selectedShape.radius !== undefined) {
      const d = toDisplayUnit(pxToMM(selectedShape.radius * 2), displayUnit);
      w = d; h = d;
    } else return null;
    return { w: w.toFixed(1), h: h.toFixed(1) };
  }, [selectedShape, displayUnit, isResizing, liveResizeSize]);

  const liveSize = getLiveSize();

  // Render node handles
  const renderNodeHandles = (shape) => {
    if (tool !== 'nodes' || selectedId !== shape.id || !shape.points) return null;
    const handles = [];
    for (let i = 0; i < shape.points.length; i += 2) {
      const x = shape.points[i], y = shape.points[i + 1], nodeIndex = i / 2;
      handles.push(
        <Circle key={`node_${shape.id}_${nodeIndex}`} x={shape.x + x} y={shape.y + y} radius={6 / zoom}
          fill={selectedNodeIndex === nodeIndex ? '#ff0000' : '#0066ff'} stroke="#ffffff" strokeWidth={2 / zoom} draggable
          onDragMove={(e) => updateNodePosition(shape.id, nodeIndex, e.target.x() - shape.x, e.target.y() - shape.y)}
          onDragEnd={() => saveToHistory(shapes)} onClick={() => setSelectedNodeIndex(nodeIndex)} />
      );
    }
    return handles;
  };

  const renderShape = (shape) => {
    if (!shape.visible) return null;
    const isWireframe = viewMode === 'wireframe';
    const commonProps = {
      key: shape.id, id: shape.id, x: shape.x, y: shape.y,
      fill: isWireframe ? 'transparent' : shape.fill, stroke: shape.stroke,
      strokeWidth: isWireframe ? 1 : shape.strokeWidth, rotation: shape.rotation,
      scaleX: shape.scaleX, scaleY: shape.scaleY,
      draggable: !shape.locked && (tool === 'select' || tool === 'nodes'),
      onClick: () => handleSelect(shape.id), onTap: () => handleSelect(shape.id),
      onDragEnd: handleDragEnd, onTransformEnd: handleTransformEnd,
      opacity: shape.locked ? 0.7 : 1, dash: isWireframe ? [5, 5] : undefined,
    };

    if (tool === 'nodes' && selectedId === shape.id && shape.points && shape.type !== 'text') {
      return <Line {...commonProps} points={shape.points} closed={['rect', 'circle', 'star'].includes(shape.type)} tension={shape.tension || 0} lineCap="round" lineJoin="round" />;
    }

    switch (shape.type) {
      case 'rect': return <Rect {...commonProps} width={shape.width} height={shape.height} cornerRadius={shape.cornerRadius || 0} />;
      case 'circle': return <Circle {...commonProps} radius={shape.radius} />;
      case 'star': return <Star {...commonProps} numPoints={shape.numPoints} innerRadius={shape.innerRadius} outerRadius={shape.outerRadius} />;
      case 'line': return <Line {...commonProps} points={shape.points} tension={shape.tension || 0} lineCap="round" lineJoin="round" />;
      case 'arrow': return <Arrow {...commonProps} points={shape.points} tension={shape.tension || 0} pointerLength={shape.pointerLength} pointerWidth={shape.pointerWidth} />;
      case 'path': return <Line {...commonProps} points={shape.points} tension={shape.tension || 0} lineCap="round" lineJoin="round" />;
      case 'text': return <Text {...commonProps} text={shape.text} fontSize={shape.fontSize} fontFamily={shape.fontFamily} fontStyle={shape.fontStyle} textDecoration={shape.textDecoration} fill={isWireframe ? 'transparent' : shape.fill} stroke={isWireframe ? shape.stroke : undefined} strokeWidth={isWireframe ? 1 : 0} />;
      case 'image': return <KonvaImage {...commonProps} image={shape.image} width={shape.width} height={shape.height} />;
      default: return null;
    }
  };

  const displayWidth = canvasWidth * zoom;
  const displayHeight = canvasHeight * zoom;

  return (
    <div className="flex flex-col gap-4" tabIndex={0} onKeyDown={(e) => {
      // Also handle delete here for better reliability
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        deleteSelected();
      }
    }}>
      {/* Top Controls */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-white rounded-xl border border-gray-200">
        <Select value={pageSize} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PAGE_SIZES).map(([key, val]) => <SelectItem key={key} value={key}>{val.name}</SelectItem>)}</SelectContent>
        </Select>

        {pageSize === 'custom' && (
          <div className="flex items-center gap-1">
            <Input type="number" value={Math.round(toDisplayUnit(canvasWidthMM, displayUnit))} onChange={(e) => setCanvasWidthMM(fromDisplayUnit(Number(e.target.value), displayUnit))} className="w-16 h-8 text-xs" min={10} />
            <span className="text-xs text-gray-400">×</span>
            <Input type="number" value={Math.round(toDisplayUnit(canvasHeightMM, displayUnit))} onChange={(e) => setCanvasHeightMM(fromDisplayUnit(Number(e.target.value), displayUnit))} className="w-16 h-8 text-xs" min={10} />
          </div>
        )}

        <Select value={displayUnit} onValueChange={setDisplayUnit}>
          <SelectTrigger className="w-20 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(UNITS).map(([key, val]) => <SelectItem key={key} value={key}>{val.symbol}</SelectItem>)}</SelectContent>
        </Select>

        <div className="w-px h-6 bg-gray-200" />

        <div className="flex items-center border rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('full')} className={`flex items-center gap-1 px-3 py-1.5 text-xs ${viewMode === 'full' ? 'bg-black text-white' : 'hover:bg-gray-100'}`}>
            <Eye className="h-3.5 w-3.5" />Preview
          </button>
          <button onClick={() => setViewMode('wireframe')} className={`flex items-center gap-1 px-3 py-1.5 text-xs ${viewMode === 'wireframe' ? 'bg-black text-white' : 'hover:bg-gray-100'}`}>
            <Grid3X3 className="h-3.5 w-3.5" />Wireframe
          </button>
        </div>

        <button onClick={() => setShowGrid(!showGrid)} className={`flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg ${showGrid ? 'bg-black text-white' : 'hover:bg-gray-100'}`}>
          <Grid3X3 className="h-3.5 w-3.5" />Grid
        </button>

        <div className="w-px h-6 bg-gray-200" />

        <div className="flex items-center gap-1">
          <button onClick={undo} disabled={historyIndex <= 0} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30"><Undo className="h-4 w-4" /></button>
          <button onClick={redo} disabled={historyIndex >= history.length - 1} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30"><Redo className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom(Math.max(0.25, zoom - 0.25))} className="p-1.5 rounded hover:bg-gray-100"><ZoomOut className="h-4 w-4" /></button>
          <span className="text-xs w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(Math.min(2, zoom + 0.25))} className="p-1.5 rounded hover:bg-gray-100"><ZoomIn className="h-4 w-4" /></button>
          <button onClick={() => setZoom(1)} className="p-1.5 rounded hover:bg-gray-100"><RotateCcw className="h-4 w-4" /></button>
        </div>

        <div className="flex-1" />

        {/* Live Size Display */}
        {liveSize && (
          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isResizing ? 'bg-green-100 text-green-700 animate-pulse' : 'bg-blue-50 text-blue-700'}`}>
            {liveSize.w} × {liveSize.h} {UNITS[displayUnit].symbol}
          </div>
        )}

        <label className="p-1.5 rounded hover:bg-gray-100 cursor-pointer"><Upload className="h-4 w-4" /><input type="file" accept=".json" onChange={loadDesign} className="hidden" /></label>
        <button onClick={saveDesign} className="p-1.5 rounded hover:bg-gray-100"><Save className="h-4 w-4" /></button>
        
        <Button onClick={() => setShowPricing(true)} className="h-8 rounded-full text-xs gap-1" data-testid="finish-price-btn">
          <Calculator className="h-3.5 w-3.5" />Finish & Price
        </Button>
      </div>

      {/* Main Editor */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left Toolbar */}
        <div className="lg:w-14 flex lg:flex-col items-center gap-1 p-2 bg-white rounded-xl border border-gray-200">
          <button onClick={() => setTool('select')} className={`p-2.5 rounded-lg ${tool === 'select' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Select (V)">
            <MousePointer className="h-5 w-5" />
          </button>
          <button onClick={() => setTool('nodes')} className={`p-2.5 rounded-lg ${tool === 'nodes' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Edit Nodes (N)">
            <PenTool className="h-5 w-5" />
          </button>
          <div className="w-full h-px bg-gray-200 my-1 hidden lg:block" />
          <button onClick={() => setTool('rect')} className={`p-2.5 rounded-lg ${tool === 'rect' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Rectangle - Click on canvas"><Square className="h-5 w-5" /></button>
          <button onClick={() => setTool('circle')} className={`p-2.5 rounded-lg ${tool === 'circle' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Circle - Click on canvas"><CircleIcon className="h-5 w-5" /></button>
          <button onClick={() => setTool('star')} className={`p-2.5 rounded-lg ${tool === 'star' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Star - Click on canvas"><StarIcon className="h-5 w-5" /></button>
          <button onClick={() => setTool('line')} className={`p-2.5 rounded-lg ${tool === 'line' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Line - Click on canvas"><Minus className="h-5 w-5" /></button>
          <button onClick={() => setTool('arrow')} className={`p-2.5 rounded-lg ${tool === 'arrow' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Arrow - Click on canvas"><ArrowRight className="h-5 w-5" /></button>
          <button onClick={() => setTool('path')} className={`p-2.5 rounded-lg ${tool === 'path' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Path - Click on canvas">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 17 C 9 3, 15 21, 21 7" /></svg>
          </button>
          <button onClick={() => setTool('text')} className={`p-2.5 rounded-lg ${tool === 'text' ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title="Text - Click on canvas"><Type className="h-5 w-5" /></button>
          <div className="w-full h-px bg-gray-200 my-1 hidden lg:block" />
          <label className="p-2.5 rounded-lg hover:bg-gray-100 cursor-pointer" title="Import Image">
            <ImageIcon className="h-5 w-5" /><input type="file" accept="image/*" onChange={importImage} className="hidden" />
          </label>
        </div>

        {/* Canvas */}
        <div className="flex-1 bg-gray-100 rounded-xl p-4 overflow-auto" style={{ minHeight: '450px' }}>
          {tool !== 'select' && tool !== 'nodes' && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-black text-white px-4 py-2 rounded-full text-sm">
              Click on canvas to add {tool}
            </div>
          )}
          <div className="mx-auto shadow-lg relative" style={{ width: displayWidth, height: displayHeight, background: viewMode === 'wireframe' ? '#f8f8f8' : '#fff' }}>
            <Stage ref={stageRef} width={canvasWidth} height={canvasHeight} scaleX={zoom} scaleY={zoom} onClick={handleCanvasClick} onTap={handleCanvasClick}>
              <Layer ref={layerRef}>
                {showGrid && [...Array(Math.ceil(canvasWidth / 50) + 1)].map((_, i) => <Line key={`v${i}`} points={[i * 50, 0, i * 50, canvasHeight]} stroke="#e5e5e5" strokeWidth={1} />)}
                {showGrid && [...Array(Math.ceil(canvasHeight / 50) + 1)].map((_, i) => <Line key={`h${i}`} points={[0, i * 50, canvasWidth, i * 50]} stroke="#e5e5e5" strokeWidth={1} />)}
                {shapes.map(renderShape)}
                {shapes.map(renderNodeHandles)}
                {tool === 'select' && (
                  <Transformer 
                    ref={trRef} 
                    boundBoxFunc={(oldBox, newBox) => newBox.width < 5 || newBox.height < 5 ? oldBox : newBox}
                    onTransform={handleTransform}
                    onTransformEnd={handleTransformEnd}
                    anchorCornerRadius={3}
                    anchorStroke="#000"
                    anchorFill="#fff"
                    anchorSize={10}
                    borderStroke="#000"
                    borderDash={[3, 3]}
                  />
                )}
              </Layer>
            </Stage>
          </div>
        </div>

        {/* Right Panel */}
        <div className="lg:w-72 flex flex-col gap-3 max-h-[600px] overflow-y-auto">
          {/* Colors */}
          <div className="p-3 bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Spot Colors</Label>
              <span className="text-xs text-gray-400">{selectedColorName}</span>
            </div>
            <div className="grid grid-cols-8 gap-1">
              {SPOT_COLORS.map((color, i) => (
                <button key={i} onClick={() => handleColorSelect(color)}
                  className={`w-6 h-6 rounded border-2 transition-transform hover:scale-110 ${drawingColor === color.hex ? 'border-black scale-110 ring-2 ring-offset-1 ring-black' : 'border-gray-200'}`}
                  style={{ backgroundColor: color.hex }} title={color.name} />
              ))}
            </div>
          </div>

          {/* Node Tools */}
          {tool === 'nodes' && selectedShape?.points && (
            <div className="p-3 bg-white rounded-xl border border-gray-200">
              <Label className="text-xs font-semibold text-gray-500 uppercase">Node Tools</Label>
              <p className="text-[10px] text-gray-400 mt-1 mb-2">
                {selectedNodeIndex !== null 
                  ? `Node ${selectedNodeIndex + 1} selected - click + to insert after`
                  : 'Click a node to select it'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="flex-1 h-7 text-xs" onClick={toggleNodeCurve}>
                  <CornerUpRight className="h-3 w-3 mr-1" />{selectedShape.tension > 0 ? 'Straighten' : 'Curve'}
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={addNode} title={selectedNodeIndex !== null ? 'Insert node after selected' : 'Add node at end'}>
                  <Plus className="h-3 w-3" />Node
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs text-red-600" onClick={removeNode} disabled={selectedNodeIndex === null}>- Node</Button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                Total: {selectedShape.points.length / 2} nodes
              </p>
            </div>
          )}

          {/* Selected Shape Properties */}
          {selectedShape && (
            <div className="p-3 bg-white rounded-xl border border-gray-200 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-gray-500 uppercase">{selectedShape.type}</Label>
                <div className="flex gap-1">
                  <button onClick={toggleLock} className="p-1 rounded hover:bg-gray-100">{selectedShape.locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}</button>
                  <button onClick={toggleVisibility} className="p-1 rounded hover:bg-gray-100">{selectedShape.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
                </div>
              </div>

              {/* Size with Arrow Controls */}
              {(selectedShape.width !== undefined || selectedShape.radius !== undefined) && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">Size ({UNITS[displayUnit].symbol})</Label>
                    {selectedShape.width !== undefined && (
                      <button onClick={() => setProportionLocked(!proportionLocked)} className={`p-1 rounded ${proportionLocked ? 'bg-black text-white' : 'hover:bg-gray-100'}`} title={proportionLocked ? 'Unlock' : 'Lock'}>
                        {proportionLocked ? <Link className="h-3.5 w-3.5" /> : <Link2Off className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                  
                  {selectedShape.width !== undefined ? (
                    <div className="space-y-2">
                      {/* Width with Left/Right arrows */}
                      <div className="flex items-center gap-1">
                        <button onClick={() => adjustSize('width', -5)} className="p-1.5 rounded hover:bg-gray-100 border"><ChevronLeft className="h-4 w-4" /></button>
                        <div className="flex-1">
                          <span className="text-[10px] text-gray-400 block text-center">Width</span>
                          <Input type="number" value={Math.round(toDisplayUnit(pxToMM(selectedShape.width), displayUnit))} 
                            onChange={(e) => updateSizeWithProportion('width', mmToPx(fromDisplayUnit(Number(e.target.value), displayUnit)))} 
                            className="h-7 text-xs text-center" min={1} />
                        </div>
                        <button onClick={() => adjustSize('width', 5)} className="p-1.5 rounded hover:bg-gray-100 border"><ChevronRight className="h-4 w-4" /></button>
                      </div>
                      
                      {/* Height with Up/Down arrows */}
                      <div className="flex items-center gap-1">
                        <button onClick={() => adjustSize('height', 5)} className="p-1.5 rounded hover:bg-gray-100 border"><ChevronUp className="h-4 w-4" /></button>
                        <div className="flex-1">
                          <span className="text-[10px] text-gray-400 block text-center">Height</span>
                          <Input type="number" value={Math.round(toDisplayUnit(pxToMM(selectedShape.height), displayUnit))} 
                            onChange={(e) => updateSizeWithProportion('height', mmToPx(fromDisplayUnit(Number(e.target.value), displayUnit)))} 
                            className="h-7 text-xs text-center" min={1} />
                        </div>
                        <button onClick={() => adjustSize('height', -5)} className="p-1.5 rounded hover:bg-gray-100 border"><ChevronDown className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button onClick={() => adjustSize('radius', -5)} className="p-1.5 rounded hover:bg-gray-100 border"><ChevronLeft className="h-4 w-4" /></button>
                      <div className="flex-1">
                        <span className="text-[10px] text-gray-400 block text-center">Radius</span>
                        <Input type="number" value={Math.round(toDisplayUnit(pxToMM(selectedShape.radius), displayUnit))} 
                          onChange={(e) => updateSelectedStyle('radius', mmToPx(fromDisplayUnit(Number(e.target.value), displayUnit)))} 
                          className="h-7 text-xs text-center" min={1} />
                      </div>
                      <button onClick={() => adjustSize('radius', 5)} className="p-1.5 rounded hover:bg-gray-100 border"><ChevronRight className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              )}

              {/* Stroke */}
              <div>
                <Label className="text-xs">Stroke: {selectedShape.strokeWidth}px</Label>
                <Slider value={[selectedShape.strokeWidth || 0]} onValueChange={([val]) => updateSelectedStyle('strokeWidth', val)} min={0} max={20} step={1} className="mt-1" />
              </div>

              {/* Text Properties */}
              {selectedShape.type === 'text' && (
                <>
                  <div>
                    <Label className="text-xs">Text</Label>
                    <Input value={selectedShape.text || ''} onChange={(e) => updateSelectedStyle('text', e.target.value)} className="mt-1 h-7 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Font</Label>
                    <Select value={selectedShape.fontFamily} onValueChange={(v) => updateSelectedStyle('fontFamily', v)}>
                      <SelectTrigger className="mt-1 h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{FONTS.map(font => <SelectItem key={font} value={font} style={{ fontFamily: font }}>{font}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Size: {selectedShape.fontSize}px</Label>
                    <Slider value={[selectedShape.fontSize || 20]} onValueChange={([val]) => updateSelectedStyle('fontSize', val)} min={8} max={200} className="mt-1" />
                  </div>
                  <div className="flex gap-1">
                    <Button variant={selectedShape.fontStyle?.includes('bold') ? 'default' : 'outline'} size="sm" className="flex-1 h-7 text-xs font-bold" onClick={() => updateSelectedStyle('fontStyle', selectedShape.fontStyle?.includes('bold') ? 'normal' : 'bold')}>B</Button>
                    <Button variant={selectedShape.fontStyle?.includes('italic') ? 'default' : 'outline'} size="sm" className="flex-1 h-7 text-xs italic" onClick={() => updateSelectedStyle('fontStyle', selectedShape.fontStyle?.includes('italic') ? 'normal' : 'italic')}>I</Button>
                    <Button variant={selectedShape.textDecoration === 'underline' ? 'default' : 'outline'} size="sm" className="flex-1 h-7 text-xs underline" onClick={() => updateSelectedStyle('textDecoration', selectedShape.textDecoration === 'underline' ? '' : 'underline')}>U</Button>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={convertTextToCurves} 
                    className="w-full h-7 text-xs gap-1"
                    title="Convert text to editable vector path"
                  >
                    <PenTool className="h-3 w-3" />Convert to Curves
                  </Button>
                </>
              )}

              {/* Actions */}
              <div className="flex gap-1 pt-1">
                <Button variant="outline" size="sm" onClick={duplicateSelected} className="flex-1 h-7 text-xs" title="Ctrl+D"><Copy className="h-3 w-3 mr-1" />Copy</Button>
                <Button variant="outline" size="sm" onClick={deleteSelected} className="flex-1 h-7 text-xs text-red-600" title="Press Delete key"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
              </div>
              <p className="text-[10px] text-gray-400 text-center">Tip: Press Delete key to remove</p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={bringToFront} className="flex-1 h-7 text-xs"><ChevronUp className="h-3 w-3" />Front</Button>
                <Button variant="outline" size="sm" onClick={sendToBack} className="flex-1 h-7 text-xs"><ChevronDown className="h-3 w-3" />Back</Button>
              </div>
            </div>
          )}

          {/* Export */}
          <div className="space-y-2">
            <Button onClick={exportSVG} className="w-full rounded-full h-9 text-xs"><Download className="h-4 w-4 mr-1" />Export SVG</Button>
            <Button onClick={exportPNG} variant="outline" className="w-full rounded-full h-9 text-xs"><Download className="h-4 w-4 mr-1" />Export PNG</Button>
          </div>
        </div>
      </div>

      {/* Pricing Dialog */}
      <Dialog open={showPricing} onOpenChange={setShowPricing}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calculator className="h-5 w-5" />Design Price Quote</DialogTitle>
            <DialogDescription>Price based on vinyl coverage area</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {shapes.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Add shapes to calculate price</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Width</p>
                    <p className="text-lg font-semibold">{pricingInfo.widthMM?.toFixed(1)} mm</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Height</p>
                    <p className="text-lg font-semibold">{pricingInfo.heightMM?.toFixed(1)} mm</p>
                  </div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Coverage Area</p>
                  <p className="text-xl font-bold">{pricingInfo.areaSQM?.toFixed(4)} m²</p>
                </div>

                <div className="p-4 bg-black text-white rounded-lg">
                  <p className="text-xs text-gray-300 mb-1">Price</p>
                  <p className="text-3xl font-bold">£{pricingInfo.price?.toFixed(2)}</p>
                  <p className="text-xs text-gray-400 mt-1">£{PRICE_PER_SQM}/m² • Min £{MIN_ORDER}</p>
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPricing(false)} className="flex-1 rounded-full">Continue</Button>
            <Button onClick={() => { exportSVG(); setShowPricing(false); }} className="flex-1 rounded-full" disabled={shapes.length === 0}>
              <Check className="h-4 w-4 mr-1" />Export & Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignStudio;
